import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Bell,
  X,
  Check,
  Archive,
  Trash2,
  Filter,
  Search,
  Clock,
  AlertCircle,
  CheckCircle,
  Info,
  AlertTriangle,
  Megaphone,
} from 'lucide-react'
import { useNotifications } from '@/contexts/NotificationContext'
import { Notification, NotificationPriority, NotificationType } from '@/types/notifications'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { cn } from '@/utils/cn'
import { useLearnerAnnouncements } from '@/hooks/useAnnouncements'
import { AnnouncementModel } from '@/models/announcement.model'
import { paths } from '@/routes/path'

interface NotificationCenterProps {
  isOpen: boolean
  onClose: () => void
}

export function NotificationCenter({ isOpen, onClose }: NotificationCenterProps) {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    deleteNotification,
    isLoading,
  } = useNotifications()

  const [activeTab, setActiveTab] = useState<'notifications' | 'announcements'>('announcements')
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState<NotificationType | 'all'>('all')
  const [filterPriority, setFilterPriority] = useState<NotificationPriority | 'all'>('all')
  const [showArchived, setShowArchived] = useState(false)
  const [selectedNotifications, setSelectedNotifications] = useState<Set<string>>(new Set())
  const [announcementPage, setAnnouncementPage] = useState(1)
  const announcementPerPage = 8

  const {
    data: learnerAnnouncements,
    isLoading: isAnnouncementsLoading,
    isFetching: isAnnouncementsFetching,
  } = useLearnerAnnouncements({ page: announcementPage, perPage: announcementPerPage })

  useEffect(() => {
    if (activeTab === 'announcements') {
      setSelectedNotifications(new Set())
    }
  }, [activeTab])

  const filteredNotifications = useMemo(() => {
    return notifications.filter((notification) => {
      const matchesSearch =
        notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.message.toLowerCase().includes(searchTerm.toLowerCase())
      const matchesType = filterType === 'all' || notification.type === filterType
      const matchesPriority = filterPriority === 'all' || notification.priority === filterPriority
      const matchesArchived = showArchived ? notification.isArchived : !notification.isArchived
      return matchesSearch && matchesType && matchesPriority && matchesArchived
    })
  }, [notifications, searchTerm, filterType, filterPriority, showArchived])

  const announcementItems = useMemo(() => {
    const list = learnerAnnouncements?.data ?? []
    return list.slice().sort((a, b) => Number(b.isPinned) - Number(a.isPinned))
  }, [learnerAnnouncements])

  if (!isOpen) return null

  const announcementMeta = learnerAnnouncements?.meta
  const announcementTotalPages = announcementMeta ? Math.max(1, Math.ceil(announcementMeta.total / announcementMeta.perPage)) : 1

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b p-4">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <h2 className="text-lg font-semibold">Notifications</h2>
              {/* {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {unreadCount}
                </Badge>
              )} */}
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="border-b px-4">
            <div className="flex gap-4">
              {(['announcements'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={cn(
                    'border-b-2 px-2 py-3 text-sm font-medium',
                    activeTab === tab
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-secondary-500 hover:text-secondary-700',
                  )}
                  onClick={() => setActiveTab(tab)}
                >
                  {'Announcements'}
                </button>
              ))}
            </div>
          </div>

          {activeTab === 'notifications' ? (
            <NotificationsPane
              notifications={filteredNotifications}
              isLoading={isLoading}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterType={filterType}
              setFilterType={setFilterType}
              filterPriority={filterPriority}
              setFilterPriority={setFilterPriority}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              selectedNotifications={selectedNotifications}
              markAllAsRead={markAllAsRead}
              handleSelectNotification={(id) => {
                setSelectedNotifications((prev) => {
                  const next = new Set(prev)
                  if (next.has(id)) next.delete(id)
                  else next.add(id)
                  return next
                })
              }}
              handleSelectAll={() => {
                setSelectedNotifications((prev) => {
                  if (prev.size === filteredNotifications.length) {
                    return new Set()
                  }
                  return new Set(filteredNotifications.map((notification) => notification.id))
                })
              }}
              handleBulkAction={(action) => {
                selectedNotifications.forEach((id) => {
                  switch (action) {
                    case 'read':
                      markAsRead(id)
                      break
                    case 'archive':
                      archiveNotification(id)
                      break
                    case 'delete':
                      deleteNotification(id)
                      break
                  }
                })
                setSelectedNotifications(new Set())
              }}
              markAsRead={markAsRead}
              archiveNotification={archiveNotification}
              deleteNotification={deleteNotification}
              unreadCount={unreadCount}
            />
          ) : (
            <AnnouncementsPane
              announcements={announcementItems}
              isLoading={isAnnouncementsLoading || isAnnouncementsFetching}
              page={announcementPage}
              totalPages={announcementTotalPages}
              onPageChange={setAnnouncementPage}
              onClose={onClose}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function NotificationsPane({
  notifications,
  isLoading,
  searchTerm,
  setSearchTerm,
  filterType,
  setFilterType,
  filterPriority,
  setFilterPriority,
  showArchived,
  setShowArchived,
  selectedNotifications,
  markAllAsRead,
  handleSelectNotification,
  handleSelectAll,
  handleBulkAction,
  markAsRead,
  archiveNotification,
  deleteNotification,
  unreadCount,
}: {
  notifications: Notification[]
  isLoading: boolean
  searchTerm: string
  setSearchTerm: (value: string) => void
  filterType: NotificationType | 'all'
  setFilterType: (value: NotificationType | 'all') => void
  filterPriority: NotificationPriority | 'all'
  setFilterPriority: (value: NotificationPriority | 'all') => void
  showArchived: boolean
  setShowArchived: (value: boolean) => void
  selectedNotifications: Set<string>
  markAllAsRead: () => void
  handleSelectNotification: (id: string) => void
  handleSelectAll: () => void
  handleBulkAction: (action: 'read' | 'archive' | 'delete') => void
  markAsRead: (id: string) => void
  archiveNotification: (id: string) => void
  deleteNotification: (id: string) => void
  unreadCount: number
}) {
  return (
    <>
      <div className="border-b p-4 space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
            <Input value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search notifications" className="pl-9" />
          </div>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filter
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select
            value={filterType}
            onChange={(event) => setFilterType(event.target.value as NotificationType | 'all')}
            className="rounded-md border border-secondary-300 px-3 py-2 text-sm"
          >
            <option value="all">All Types</option>
            <option value="assignment_reminder">Assignment Reminders</option>
            <option value="assignment_due">Assignment Due</option>
            <option value="assignment_graded">Assignment Graded</option>
            <option value="course_update">Course Updates</option>
            <option value="course_announcement">Course Announcements</option>
            <option value="discussion_reply">Discussions</option>
            <option value="system_maintenance">System</option>
          </select>
          <select
            value={filterPriority}
            onChange={(event) => setFilterPriority(event.target.value as NotificationPriority | 'all')}
            className="rounded-md border border-secondary-300 px-3 py-2 text-sm"
          >
            <option value="all">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div className="flex items-center justify-between text-sm text-secondary-600">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(event) => setShowArchived(event.target.checked)}
              className="rounded border-secondary-300 text-primary-600"
            />
            Show archived
          </label>
          <Button variant="ghost" size="sm" onClick={markAllAsRead} disabled={unreadCount === 0}>
            <Check className="mr-1 h-4 w-4" />
            Mark all read
          </Button>
        </div>
      </div>

      {notifications.length > 0 && (
        <div className="border-b p-4 flex flex-wrap items-center justify-between gap-2 text-sm text-secondary-600">
          <span>{selectedNotifications.size} selected</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleSelectAll}>
              {selectedNotifications.size === notifications.length ? 'Deselect' : 'Select all'}
            </Button>
            {selectedNotifications.size > 0 && (
              <>
                <Button variant="outline" size="sm" onClick={() => handleBulkAction('read')}>
                  Mark read
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleBulkAction('archive')}>
                  Archive
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleBulkAction('delete')}>
                  Delete
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-secondary-400">Loading notifications…</div>
        ) : notifications.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-secondary-500">
            <Bell className="mb-3 h-8 w-8" />
            <p>No notifications yet</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <Card
              key={notification.id}
              className={cn(
                'transition',
                notification.isRead ? 'bg-white' : 'bg-primary-50 border-primary-100',
                selectedNotifications.has(notification.id) && 'ring-2 ring-primary-500',
              )}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={selectedNotifications.has(notification.id)}
                    onChange={() => handleSelectNotification(notification.id)}
                    className="mt-1 h-4 w-4 rounded border-secondary-300 text-primary-600"
                  />
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    {getNotificationIcon(notification.type)}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base">{notification.title}</CardTitle>
                        <CardDescription className="text-xs">
                          {getTypeLabel(notification.type)} • {getPriorityLabel(notification.priority)}
                        </CardDescription>
                      </div>
                      {!notification.isRead && <Badge variant="default">New</Badge>}
                    </div>
                    <p className="mt-2 text-sm text-secondary-600">{notification.message}</p>
                    <div className="mt-3 flex items-center gap-2 text-xs text-secondary-500">
                      <span>{formatTimeAgo(notification.createdAt)}</span>
                      {notification.priority && <Badge className={getPriorityColor(notification.priority)}>{notification.priority}</Badge>}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {!notification.isRead && (
                        <Button variant="ghost" size="sm" onClick={() => markAsRead(notification.id)}>
                          <CheckCircle className="mr-1 h-4 w-4" />
                          Mark read
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => archiveNotification(notification.id)}>
                        <Archive className="mr-1 h-4 w-4" />
                        Archive
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteNotification(notification.id)}>
                        <Trash2 className="mr-1 h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </>
  )
}

function AnnouncementsPane({
  announcements,
  isLoading,
  page,
  totalPages,
  onPageChange,
  onClose,
}: {
  announcements: AnnouncementModel[]
  isLoading: boolean
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  onClose: () => void
}) {
  return (
    <>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <div className="flex h-full items-center justify-center text-secondary-400">Loading announcements…</div>
        ) : announcements.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center text-secondary-500">
            <Megaphone className="mb-3 h-8 w-8" />
            <p>No announcements available.</p>
          </div>
        ) : (
          announcements.map((announcement) => (
            <Card key={announcement.id} className="border border-secondary-100">
              <CardContent className="space-y-2 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {announcement.isPinned && <Badge className="bg-warning-100 text-warning-700">Pinned</Badge>}
                    <CardTitle className="text-base">{announcement.title}</CardTitle>
                  </div>
                  <span className="text-xs text-secondary-400">
                    {announcement.createdAt ? new Date(announcement.createdAt).toLocaleDateString() : ''}
                  </span>
                </div>
                {announcement.courseTitle && <CardDescription>Course: {announcement.courseTitle}</CardDescription>}
                <p className="text-sm text-secondary-600 line-clamp-3">{announcement.content}</p>
                <div className="flex items-center justify-between text-xs text-secondary-500">
                  <span>{announcement.authorName}</span>
                  <Link to={paths.app.announcements.detail.build(announcement.id)} onClick={onClose}>
                    <Button variant="ghost" size="sm">
                      View
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      <div className="flex items-center justify-between border-t p-4 text-sm text-secondary-600">
        <Button variant="ghost" size="sm" onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page <= 1 || isLoading}>
          Previous
        </Button>
        <span>
          Page {page} of {totalPages}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages || isLoading}
        >
          Next
        </Button>
      </div>
    </>
  )
}

const getNotificationIcon = (type: NotificationType) => {
  switch (type) {
    case 'assignment_reminder':
    case 'assignment_due':
    case 'assignment_graded':
      return <Clock className="h-4 w-4" />
    case 'course_update':
    case 'course_announcement':
    case 'course_material_added':
      return <Info className="h-4 w-4" />
    case 'discussion_reply':
    case 'discussion_mention':
      return <AlertCircle className="h-4 w-4" />
    case 'system_maintenance':
      return <AlertTriangle className="h-4 w-4" />
    default:
      return <Bell className="h-4 w-4" />
  }
}

const getPriorityColor = (priority: NotificationPriority) => {
  switch (priority) {
    case 'urgent':
      return 'bg-red-100 text-red-800'
    case 'high':
      return 'bg-orange-100 text-orange-800'
    case 'medium':
      return 'bg-yellow-100 text-yellow-800'
    case 'low':
      return 'bg-green-100 text-green-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getTypeLabel = (type: NotificationType) => {
  switch (type) {
    case 'assignment_reminder':
      return 'Assignment Reminder'
    case 'assignment_due':
      return 'Assignment Due'
    case 'assignment_graded':
      return 'Assignment Graded'
    case 'course_update':
      return 'Course Update'
    case 'course_announcement':
      return 'Course Announcement'
    case 'course_material_added':
      return 'New Material'
    case 'discussion_reply':
      return 'Discussion Reply'
    case 'discussion_mention':
      return 'Mentioned'
    case 'system_maintenance':
      return 'System Maintenance'
    default:
      return 'General'
  }
}

const getPriorityLabel = (priority: NotificationPriority) => {
  switch (priority) {
    case 'urgent':
      return 'Urgent'
    case 'high':
      return 'High'
    case 'medium':
      return 'Medium'
    case 'low':
      return 'Low'
    default:
      return 'General'
  }
}

const formatTimeAgo = (dateString: string) => {
  const now = new Date()
  const date = new Date(dateString)
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
  if (diffInMinutes < 1) return 'Just now'
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  const diffInHours = Math.floor(diffInMinutes / 60)
  if (diffInHours < 24) return `${diffInHours}h ago`
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `${diffInDays}d ago`
  return date.toLocaleDateString()
}
